import { useMemo } from 'react';
import type { DdqSession, SessionPriority } from '@/types/api.types';
import { sortSessions, type SessionSortOption } from '../utils/sortSessions';

/**
 * Inclusive date range for filtering sessions by creation date.
 * Both bounds are optional; null means unbounded on that side.
 */
export interface DateRange {
  from: string | null; // ISO date string (YYYY-MM-DD)
  to: string | null;   // ISO date string (YYYY-MM-DD)
}

/**
 * Combined filter and sort state for the session list.
 */
export interface SessionFilters {
  searchQuery: string;
  selectedStatuses: DdqSession['status'][];
  selectedPriorities?: SessionPriority[];
  sortBy: SessionSortOption;
  dateRange: DateRange;
}

/**
 * Filters and sorts a list of DDQ sessions based on the provided filter state.
 *
 * Applies search, status, priority, and date range filters in sequence,
 * then delegates final ordering to `sortSessions`.
 *
 * @param sessions - Full list of sessions to filter.
 * @param filters - Active filter and sort configuration.
 * @returns Filtered and sorted subset of `sessions`.
 */
export function useFilteredSessions(
  sessions: DdqSession[],
  filters: SessionFilters
): DdqSession[] {
  return useMemo(() => {
    let filtered = [...sessions];

    // Apply search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter((session) =>
        session.fileName.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filters.selectedStatuses.length > 0) {
      filtered = filtered.filter((session) =>
        filters.selectedStatuses.includes(session.status)
      );
    }

    // Apply priority filter (v2.0)
    if (filters.selectedPriorities && filters.selectedPriorities.length > 0) {
      filtered = filtered.filter((session) => {
        const sessionPriority = (session as DdqSession & { priority?: string }).priority || 'normal';
        return filters.selectedPriorities!.includes(sessionPriority as SessionPriority);
      });
    }

    // Apply date range filter
    if (filters.dateRange.from || filters.dateRange.to) {
      filtered = filtered.filter((session) => {
        const sessionDate = new Date(session.created);
        sessionDate.setHours(0, 0, 0, 0);

        if (filters.dateRange.from) {
          const fromDate = new Date(filters.dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          if (sessionDate < fromDate) return false;
        }

        if (filters.dateRange.to) {
          const toDate = new Date(filters.dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          if (sessionDate > toDate) return false;
        }

        return true;
      });
    }

    // Apply sort
    return sortSessions(filtered, filters.sortBy);
  }, [sessions, filters.searchQuery, filters.selectedStatuses, filters.selectedPriorities, filters.sortBy, filters.dateRange]);
}
